// src/components/WavesBackground.js
import { useEffect, useRef } from 'react';

/**
 * Olas de fondo, en el color de la marca.
 *
 * La versión anterior dibujaba tres olas rellenando cada una hasta el borde
 * inferior con la misma opacidad. Superpuestas daban un bloque plano —esa
 * mancha diagonal— en vez de tres olas. Además corría a sesenta cuadros por
 * segundo sin parar nunca, no miraba si el sistema pide menos movimiento, y en
 * pantalla retina salía borrosa porque el canvas no se escalaba.
 *
 * Ahora cada capa tiene su propia opacidad —la de atrás casi no se ve, la de
 * adelante un poco más— y eso es lo que da profundidad. Se desvanecen hacia
 * arriba con un degradado vertical, así que la ola se funde con la tarjeta en
 * lugar de cortarse contra el título.
 *
 * El movimiento es lento a propósito: ciclos de más de medio minuto. Esto es
 * una herramienta de trabajo, no una portada; la onda tiene que dar atmósfera
 * sin que nadie la mire. Y se detiene sola cuando la pestaña pasa a segundo
 * plano o cuando el sistema pide reducir movimiento.
 */

const OLAS = [
  // y: dónde arranca (proporción del alto) · largo: cuántas ondas entran a lo ancho
  // alto: cuánto sube y baja · velocidad: qué tan rápido se desplaza · alpha: qué tan visible
  //
  // Van bien repartidas a lo alto y con alfas chicos a propósito. Amontonadas
  // abajo, las tres sumaban su opacidad contra el mismo borde y el resultado era
  // una franja sólida en vez de tres olas.
  { y: 0.42, largo: 1.3, alto: 12, velocidad: 0.00022, alpha: 0.045 },
  { y: 0.62, largo: 0.85, alto: 17, velocidad: 0.00034, alpha: 0.055 },
  { y: 0.84, largo: 0.55, alto: 22, velocidad: 0.00048, alpha: 0.075 }
];

/** '#F5A623' -> '245, 166, 35', para poder darle alfa a cada capa. */
const aRgb = (hex) => {
  const n = hex.replace('#', '');
  const v = n.length === 3 ? n.split('').map((c) => c + c).join('') : n;
  const num = parseInt(v, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
};

const WavesBackground = ({ color = '#F5A623' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const rgb = aRgb(color);

    // En pantallas retina el canvas tiene el doble de píxeles reales que de
    // píxeles CSS. Sin esto, la curva sale con el borde dentado.
    let ancho = 0;
    let alto = 0;
    const medir = () => {
      const padre = canvas.parentElement;
      if (!padre) return;
      const dpr = window.devicePixelRatio || 1;
      ancho = padre.offsetWidth;
      alto = padre.offsetHeight;
      canvas.width = ancho * dpr;
      canvas.height = alto * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    medir();

    const menosMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const dibujar = (t) => {
      ctx.clearRect(0, 0, ancho, alto);

      OLAS.forEach((ola) => {
        const base = ola.y * alto;

        ctx.beginPath();
        ctx.moveTo(0, alto);
        for (let x = 0; x <= ancho; x += 2) {
          const fase = (x / ancho) * ola.largo * Math.PI * 2;
          ctx.lineTo(x, base + Math.sin(fase + t * ola.velocidad) * ola.alto);
        }
        ctx.lineTo(ancho, alto);
        ctx.closePath();

        // Se desvanece hacia arriba: la cresta apenas se insinúa y abajo tiene
        // cuerpo. Es lo que evita que la onda se vea como una franja pegada.
        const degradado = ctx.createLinearGradient(0, base - ola.alto, 0, alto);
        degradado.addColorStop(0, `rgba(${rgb}, 0)`);
        degradado.addColorStop(0.55, `rgba(${rgb}, ${ola.alpha})`);
        // Se afloja de nuevo contra el borde inferior: si las tres llegan a su
        // máximo en la misma línea, lo que se ve es un zócalo de color.
        degradado.addColorStop(1, `rgba(${rgb}, ${ola.alpha * 0.45})`);
        ctx.fillStyle = degradado;
        ctx.fill();
      });
    };

    let cuadro;
    const animar = (t) => {
      dibujar(t);
      cuadro = requestAnimationFrame(animar);
    };

    const arrancar = () => {
      if (menosMovimiento || document.hidden) {
        dibujar(0); // una sola vez, quieta
        return;
      }
      cuadro = requestAnimationFrame(animar);
    };

    const detener = () => {
      if (cuadro) cancelAnimationFrame(cuadro);
      cuadro = null;
    };

    // Nada de gastar batería dibujando lo que nadie está viendo.
    const alCambiarVisibilidad = () => (document.hidden ? detener() : arrancar());
    const alRedimensionar = () => { medir(); if (menosMovimiento || document.hidden) dibujar(0); };

    arrancar();
    window.addEventListener('resize', alRedimensionar);
    document.addEventListener('visibilitychange', alCambiarVisibilidad);

    return () => {
      detener();
      window.removeEventListener('resize', alRedimensionar);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [color]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

export default WavesBackground;
